import React from 'react'
import { dayjs } from '@mobi/utils/date'
import { TicketDisplayContext, TicketDisplayContextType } from '@core/Areas/AccountActivity/context'
import { BetInfo } from '@core/Areas/AccountActivity/Components/Ticket/Components/BetInfo'
import { SelectionInfo } from '@core/Areas/AccountActivity/Components/Ticket/Components/Details/Components/SelectionInfo'
import {
  getTicketLayoutType,
  type TicketLayoutType,
} from '@core/Areas/AccountActivity/Components/Ticket/helpers/legTypes'
import type { BetLoopShareTicket } from '@core/Areas/BetLoop/types'
import { trackBetLoopExpandSelection } from '@core/Areas/BetLoop/helpers/analytics'
import { getBetLoopTicketStatus } from '@core/Areas/BetLoop/helpers/getBetLoopTicketStatus'
import { HeightTruncateWrapper } from './Components/HeightTruncateWrapper'
import { BetSelectionContainer } from '../Common/BetSelectionContainer'

export const BetSelectionDisplay: React.FC<{
  ticket: BetLoopShareTicket
  isResulted: boolean
  onPendingStatusChange(): void
  shouldDisableHeightTruncate?: true
  isForExternalShare?: boolean
}> = ({
  ticket,
  isResulted,
  onPendingStatusChange,
  shouldDisableHeightTruncate,
  isForExternalShare,
}) => {
  const ticketTypeLayout = getTicketLayoutType(ticket)

  const isHeightTruncateEnabled =
    !shouldDisableHeightTruncate && shouldEnableHeightTruncate(ticket, ticketTypeLayout)

  const handleOnChangeCallback = (expanded: boolean) => {
    trackBetLoopExpandSelection(expanded ? 'enable' : 'disable')
  }

  const ticketResultStatus = getBetLoopTicketStatus(ticket)
  const winShareResult = isResulted && ticketResultStatus === 'Won'
  const isPending = ticketResultStatus === 'Pending'
  const noPayoutShareResult = isResulted && ticketResultStatus !== 'Won'
  const hasMultipleLegToteBetStarted =
    (!isResulted &&
      ticket.allUpStartTimes?.some(ast => dayjs(ast).isBefore(dayjs().subtract(2, 'minute')))) ||
    false

  const contextValue = React.useMemo<TicketDisplayContextType>(() => {
    const forcedStatus =
      (isResulted && isPending) || hasMultipleLegToteBetStarted
        ? 'pending'
        : isResulted
          ? 'resulted'
          : undefined

    return {
      type: 'share',
      data: {
        shouldHideCost: true,
        display: isForExternalShare ? 'external-share' : 'bet-loop',
        forcedStatus,
      },
    }
  }, [isResulted, isForExternalShare, isPending, hasMultipleLegToteBetStarted])

  React.useEffect(() => {
    if (contextValue.type === 'share' && contextValue.data.forcedStatus === 'pending') {
      onPendingStatusChange()
    }
  }, [onPendingStatusChange, contextValue])

  return (
    <BetSelectionContainer.Container
      styleOverride={isHeightTruncateEnabled ? { paddingBottom: '1px' } : undefined}
      displayStatus={winShareResult ? 'win' : noPayoutShareResult ? 'loss' : null}
    >
      <HeightTruncateWrapper
        isEnabled={isHeightTruncateEnabled}
        onChangeCallback={handleOnChangeCallback}
      >
        <TicketDisplayContext.Provider value={contextValue}>
          <BetSelectionContainer.Inner>
            <BetInfo
              ticket={ticket}
              status={ticketResultStatus}
              ticketLayoutType={ticketTypeLayout}
            />

            <SelectionInfo ticket={ticket} />
          </BetSelectionContainer.Inner>
        </TicketDisplayContext.Provider>
      </HeightTruncateWrapper>
    </BetSelectionContainer.Container>
  )
}

// =============
// Local Helpers
// =============

function shouldEnableHeightTruncate(
  ticket: BetLoopShareTicket,
  layoutType: TicketLayoutType
): boolean {
  if (layoutType === 'multi' && ticket.legs.length > 2) return true
  if (layoutType === 'single-multiple') {
    const acceptorCount = ticket.legs[0].selections.reduce(
      (count, selection) => selection.selectionDetails[0].acceptorDetails.length + count,
      0
    )
    return acceptorCount > 6
  }

  return false
}
